import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  CONTROL_STAGES,
  parseControlsByStage,
  serializeControlsByStage,
} from '../constants/riskAssessment';

// Auto-sizing textarea for one control bullet.
//   - rows=2 floor so empty bullets show generous typing room.
//   - scrollHeight-based grow on every value change.
//   - capped at 160 px; beyond that the textarea scrolls internally so a
//     runaway paste doesn't bulldoze its neighbours.
function BulletInput({ value, onChange, onBlur, disabled, placeholder, className, style }) {
  const ref = useRef(null);
  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, []);
  useEffect(() => { resize(); }, [value, resize]);

  return (
    <textarea
      ref={ref}
      rows={2}
      value={value}
      disabled={disabled}
      onChange={(e) => { onChange(e.target.value); resize(); }}
      onBlur={onBlur}
      placeholder={placeholder}
      className={className}
      style={{ overflowY: 'auto', maxHeight: '160px', ...style }}
    />
  );
}

/**
 * 3-stage controls editor (or read-only viewer).
 *
 * Props:
 *  - value: string — the canonical "[Stage 1] ... [Stage 2] ... [Stage 3] ..."
 *      controls string. Legacy text without markers parks in Stage 3.
 *  - onChange?(nextString): optional — fires on every keystroke.
 *  - onCommit?(nextString): fires when the edit should be persisted
 *      (input blur, add bullet, remove bullet).
 *  - disabled: bool
 *  - readOnly: bool — when true, no editing controls; just renders the
 *      structured bullets. Used in library viewer + (eventually) print.
 *  - compact: bool — when true, stacks tighter for cramped grid cells.
 */
const STAGE_TONES = {
  1: {
    pillBg: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
    bullet: 'text-emerald-600 dark:text-emerald-300',
    accent: 'border-emerald-200 dark:border-emerald-800/50',
  },
  2: {
    pillBg: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
    bullet: 'text-sky-600 dark:text-sky-300',
    accent: 'border-sky-200 dark:border-sky-800/50',
  },
  3: {
    pillBg: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    bullet: 'text-amber-600 dark:text-amber-300',
    accent: 'border-amber-200 dark:border-amber-800/50',
  },
};

export default function ControlsByStage({
  value,
  onChange,
  onCommit,
  disabled = false,
  readOnly = false,
  compact = false,
}) {
  const initial = useMemo(() => parseControlsByStage(value), [value]);
  const [stages, setStages] = useState(initial);

  // Re-sync if parent's value changes (e.g. after server refresh).
  useEffect(() => {
    setStages(parseControlsByStage(value));
  }, [value]);

  const commit = useCallback(
    (next) => {
      const str = serializeControlsByStage(next);
      onChange?.(str);
      onCommit?.(str);
    },
    [onChange, onCommit]
  );

  const setBullet = (stage, idx, text) => {
    setStages((prev) => ({
      ...prev,
      [stage]: prev[stage].map((b, i) => (i === idx ? text : b)),
    }));
  };

  const blurBullet = (stage) => {
    // Use current stages; serialize on blur to avoid hammering on each keystroke.
    setStages((prev) => {
      commit(prev);
      return prev;
    });
  };

  const addBullet = (stage) => {
    setStages((prev) => {
      const next = { ...prev, [stage]: [...prev[stage], ''] };
      // Don't commit yet — user hasn't typed; commit on the blur of the new input.
      return next;
    });
  };

  const removeBullet = (stage, idx) => {
    setStages((prev) => {
      const next = { ...prev, [stage]: prev[stage].filter((_, i) => i !== idx) };
      commit(next);
      return next;
    });
  };

  if (readOnly) {
    const anyContent = CONTROL_STAGES.some((s) => (stages[s.n] || []).length > 0);
    if (!anyContent) {
      return (
        <div className="text-xs italic text-gray-400 dark:text-gray-500">
          No controls recorded.
        </div>
      );
    }
    return (
      <div className={compact ? 'space-y-1.5' : 'space-y-2'}>
        {CONTROL_STAGES.map((s) => {
          const items = stages[s.n] || [];
          if (items.length === 0) return null;
          const tone = STAGE_TONES[s.n];
          return (
            <div key={s.n}>
              <div className="flex items-baseline gap-2 mb-1">
                <span
                  className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${tone.pillBg}`}
                >
                  Stage {s.n}
                </span>
                <span className="text-[11px] text-gray-500 dark:text-gray-400">
                  {s.label}
                </span>
              </div>
              <ul className="ml-1 space-y-0.5">
                {items.map((b, i) => (
                  <li key={i} className="text-sm text-gray-700 dark:text-gray-300 flex gap-1.5">
                    <span className={tone.bullet}>•</span>
                    <span className="flex-1">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-2 ${compact ? 'text-sm' : ''}`}>
      {CONTROL_STAGES.map((s) => {
        const items = stages[s.n] || [];
        const tone = STAGE_TONES[s.n];
        return (
          <div
            key={s.n}
            className={`rounded-md border ${tone.accent} bg-white/60 dark:bg-gray-800/30 p-2`}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-baseline gap-2">
                <span
                  className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${tone.pillBg}`}
                >
                  Stage {s.n}
                </span>
                <span className="text-[11px] text-gray-600 dark:text-gray-300 font-medium">
                  {s.label}
                </span>
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => addBullet(s.n)}
                  className="text-[11px] text-indigo-700 dark:text-indigo-300 hover:underline"
                >
                  + Add
                </button>
              )}
            </div>
            {items.length === 0 ? (
              <div className="text-[11px] italic text-gray-500 dark:text-gray-400 px-1 py-0.5 leading-snug">
                {s.emptyHint || 'None yet.'}
              </div>
            ) : (
              <ul className="space-y-1">
                {items.map((b, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className={`mt-2 leading-none ${tone.bullet}`}>•</span>
                    <BulletInput
                      value={b}
                      onChange={(next) => setBullet(s.n, i, next)}
                      onBlur={() => blurBullet(s.n)}
                      disabled={disabled}
                      placeholder="Describe the control…"
                      className="flex-1 min-w-0 px-1.5 py-1 text-sm bg-transparent text-gray-800 dark:text-gray-200 border border-transparent rounded hover:border-gray-200 focus:border-indigo-400 focus:bg-white dark:focus:bg-gray-900 focus:outline-none resize-none leading-snug"
                    />
                    {!disabled && (
                      <button
                        type="button"
                        onClick={() => removeBullet(s.n, i)}
                        title="Remove this control"
                        className="shrink-0 px-1.5 py-0.5 text-[11px] text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      >
                        ×
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
